define((require) => {
    "use strict";

    const TweenMax                    = require("TweenMax");
    const UrlUtil                     = require("shared/util/UrlUtil");
    const EventDispatcher             = require("shared/util/EventDispatcher");
    const BrowserCapabilitiesDetector = require("shared/util/BrowserCapabilitiesDetector");

    const ANIMATION_END_EVENT_NAME = "animationend";

    const userAgent = window.navigator.userAgent;

    const capabilities = BrowserCapabilitiesDetector.detect(userAgent);

    /**
     * Browser abstraction for looking up information on the browser. <p>
     *
     * User <tt>Browser.capabilities</tt> to query for capabilities instead of creating new capabilities on the fly.
     *
     * @class Browser
     */
    const Browser = EventDispatcher.extend({

        /**
         * The browser's user agent.
         *
         * @type {String}
         */
        userAgent,

        /**
         * The detected browser capabilities.
         *
         * @type {*}
         */
        capabilities, // viewer

        /**
         * Information about the currently used browser. Can be used like `Browser.type.edge`.
         * See: browserDetector.js
         * Whenever possible, we should use feature detection rather than browser detection.
         *
         * @type {*}
         */
        type: capabilities.browser,

        /**
         * For readability we also give access via `.os` and `.environment` for values where it
         * makes sense.
         *
         * @type {*}
         */
        os: {
            android: capabilities.browser.android,
            iOS:     capabilities.browser.iOS,
            macOS:   capabilities.browser.macOS,
            winOS:   capabilities.browser.winOS,
            version: capabilities.browser.osVersion
        },

        /**
         * See above.
         *
         * @type {*}
         */
        environment: {
            desktop: capabilities.browser.desktop,
            tablet:  capabilities.browser.tablet,
            mobile:  capabilities.browser.mobile
        },

        /**
         * Returns the device pixel ratio or 1 if not specified.
         *
         * @returns {Number} the device pixel ratio.
         */
        devicePixelRatio() {
            return window.devicePixelRatio || 1.0;
        },

        /**
         * Applies the specified transform options to the provided <tt>HTMLElement</tt>.
         *
         * @param  {HTMLElement|jQuery} element The <tt>HTMLElement</tt> (or jQuery wrapped) instance.
         * @param  {Object} options The transform options, e.g.: <tt>{x: 100, y: 100, scale: 0.5}</tt>.
         *
         * @see  http://greensock.com/docs/#/HTML5/GSAP/TweenMax/set/
         */
        transform(element, options) {
            options.force3D = this.capabilities.is3dAnimationsSupported;
            // Delegate to TweenMax for consistency - it also handles any style prefixing
            // TODO: It would be great if it is possible to remove this dependency?
            TweenMax.set(element, options);
        },

        /**
         * Gets the name of the animation end event in this browser.
         * @returns {String} The name of the animation end event in this browser.
         */
        animationEndEventName() {
            return ANIMATION_END_EVENT_NAME;
        },

        createElement(tagName, config) {
            const element = document.createElement(tagName);

            element.innerHTML = config.html || "";
            element.className = config.className || "";

            if (config.contentEditable !== undefined) {
                element.setAttribute("contenteditable", config.contentEditable);
            }

            return element;
        },

        /**
         * Disables scaling of the document's viewport by setting the <tt>initial-scale</tt> and
         * <tt>maximum-scale</tt> (of the viewport meta tag/element) to 1.<p>
         *
         * This method assumes that a viewport meta tag is already present in the DOM.
         */
        disableViewportScaling() {
            const viewport = window.document.querySelector("meta[name=viewport]");

            if (viewport) {
                viewport.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1");
            }
        },

        /**
         * Gets the computed style of an element.
         * @param {HTMLElement} element The element.
         * @returns {CSSStyleDeclaration} The element style.
         */
        computedStyle(element) {
            return window.getComputedStyle(element, null);
        },

        /**
         * Adds a node to the end of the list of children of body object.
         * @param {HTMLElement} element The element to be added.
         */
        appendElement(element) {
            window.document.body.appendChild(element);
        },

        /**
         * Removes a node to the end of the list of children of body object.
         * @param {HTMLElement} element The element to be removed.
         */
        removeElement(element) {
            window.document.body.removeChild(element);
        },

        /**
         * Sets the window title. This is the experience name.
         * @param {String} title The title to set
         */
        setTitle(title) {
            window.document.title = title;
            // If we're in an iframe we want to set the title attribute.
            if (window.frameElement && typeof window.frameElement.title === "string"
                && window.frameElement.title.length === 0) {
                window.frameElement.title = title;
            }
        },

        setLanguage(language) {
            document.documentElement.setAttribute("lang", language);
        },

        /**
         * Returns an map containing all query parameters and its values from the current browser URL.
         *
         * @return {Object) A object with a all query parameters.
         */
        getQueryParameters() {
            return UrlUtil.location(window.location.href, "query");
        },

        /**
         * Wraps the window.alert function to display a JavaScript popup.
         * @param {String} msg - Message to be displayed.
         */
        alert(msg) {
            window.alert(msg);
        },

        /**
         * Wraps the window.confirm function to display a JavaScript confirm popup with conditional choice.
         * @param {String} msg - Message to be displayed.
         * @returns {Boolean} -true if selecting OK in the confirm popup.
         */
        confirm(msg) {
            return window.confirm(msg);
        },

        /**
         * Will return the asked for storage (window.localStorage or
         * window.sessionStorage), if it is available and writeable. If the
         * user has blocked cookies and saving locally, then it will return a
         * dummy object, with an interface resembling the asked for storage.
         *
         * @param {String} storageType  - Either "localStorage" or "sessionStorage".
         * @return {Storage|Object}     - Either the asked for storage, or a
         *                                dummy with an interface mimicking a
         *                                real storage.
         */
        getSafeStorage(storageType) {
            try {
                return window[storageType];
            } catch (e) {
                // Standard sessionStorage/localStorage interface
                return {
                    setItem: () => {},
                    getItem: () => {},
                    removeItem: () => {},

                    keys:       [],
                    length:     0
                };
            }
        },

        /**
         * Disable the browser spellcheck that is by default enabled for textarea and contenteditable element
         *
         */
        disableNativeSpellChecker() {
            window.document.body.setAttribute("spellcheck", false);
        }
    });

    return new Browser();
});



// WEBPACK FOOTER //
// ./src/shared/services/Browser.js